import React, { useCallback, useMemo, useRef } from 'react';
import { useMentions } from '../../hooks';
import { useTextParsing } from '../../hooks';
import type { User } from '../../utils/userUtils';
import type {
  TextAreaElementChangeHandler,
  TextAreaElementKeyboardHandler,
  TextAreaElementFocusHandler
} from '../TextAreaComponent/TextArea.types';
import { createSyntheticChangeEvent } from '../TextAreaComponent/TextArea.types';
import type { InputActionBarConfig } from '../InputActionBar/InputActionBar.types';
import { hasActionBar, withCancelClickFocus } from '../../utils/actionBarUtils';

interface UseTextAreaWithSuggestionsArgs {
  value: string;
  enableMentions: boolean;
  enableHyperlinks: boolean;
  usersApiEndpoint: string;
  debounceDelay: number;
  maxSuggestions: number;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onSubmit?: (value: string) => void;
  onMentionsChange?: (mentions: any[]) => void;
  onHyperlinksChange?: (links: any[]) => void;
  actionBarConfig?: InputActionBarConfig;
  /** Optional refocus callback registration (e.g. after file picker closes). Called with focus fn or null to unregister. */
  registerRefocusInput?: (fn: (() => void) | null) => void;
}

export function useTextAreaWithSuggestions({
  value,
  enableMentions,
  enableHyperlinks,
  usersApiEndpoint,
  debounceDelay,
  maxSuggestions,
  onChange,
  onKeyDown,
  onBlur,
  onSubmit,
  onMentionsChange,
  onHyperlinksChange,
  actionBarConfig,
  registerRefocusInput
}: UseTextAreaWithSuggestionsArgs) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    registerRefocusInput?.(() => textareaRef.current?.focus());
    return () => registerRefocusInput?.(null);
  }, [registerRefocusInput]);

  const {
    popupState,
    users,
    loading,
    mentions,
    selectedIndex,
    handleInputChange,
    handleKeyDown: mentionKeyDown,
    insertMention,
    closePopup,
    updateCaretPosition
  } = useMentions(
    value,
    newValue => {
      if (onChange && textareaRef.current) {
        const syntheticEvent = createSyntheticChangeEvent(
          textareaRef.current,
          newValue
        );
        onChange(syntheticEvent);
      }
    },
    textareaRef as any,
    debounceDelay,
    usersApiEndpoint,
    maxSuggestions
  );

  const { extractHyperlinks } = useTextParsing();

  const handleChange: TextAreaElementChangeHandler = useCallback(
    event => {
      const newValue = event.target.value;
      const caretPos = event.target.selectionStart || 0;
      if (enableMentions) {
        handleInputChange(newValue, caretPos);
      }
      onChange?.(event);
    },
    [enableMentions, handleInputChange, onChange]
  );

  const handleKeyDownEvent: TextAreaElementKeyboardHandler = useCallback(
    event => {
      let keyHandled = false;
      if (enableMentions && popupState.isOpen) {
        keyHandled = mentionKeyDown(event.nativeEvent);
        if (keyHandled) {
          event.preventDefault();
          return;
        }
      }
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        onSubmit &&
        !popupState.isOpen
      ) {
        event.preventDefault();
        onSubmit(value);
        return;
      }
      updateCaretPosition();
      onKeyDown?.(event);
    },
    [
      enableMentions,
      popupState.isOpen,
      mentionKeyDown,
      updateCaretPosition,
      onKeyDown,
      onSubmit,
      value
    ]
  );

  const handleBlur: TextAreaElementFocusHandler = useCallback(
    event => {
      onBlur?.(event);
      setTimeout(() => closePopup(), 300);
    },
    [onBlur, closePopup]
  );

  const handleMentionSelect = useCallback(
    (user: User) => {
      insertMention(user);
      setTimeout(() => textareaRef.current?.focus(), 0);
    },
    [insertMention]
  );

  const hasActionBarFlag = useMemo(
    () => hasActionBar(actionBarConfig),
    [actionBarConfig]
  );

  const effectiveOnSendClick = useCallback(() => {
    if (onSubmit && !popupState.isOpen) {
      onSubmit(value);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [value, onSubmit, popupState.isOpen]);

  const focusAfter = useCallback(() => {
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, []);

  const actionBarConfigForBar = useMemo(() => {
    if (!actionBarConfig) return undefined;
    const base = withCancelClickFocus(actionBarConfig, focusAfter);
    return base.onUploadClick
      ? {
          ...base,
          onUploadClick: () => {
            base.onUploadClick?.();
            focusAfter();
          }
        }
      : base;
  }, [actionBarConfig, focusAfter]);

  const handlePlusMenuSelect = useCallback(
    (optionId: string) => {
      textareaRef.current?.focus();
      actionBarConfig?.onPlusMenuSelect?.(optionId);
    },
    [actionBarConfig?.onPlusMenuSelect]
  );

  React.useEffect(() => {
    onMentionsChange?.(mentions);
  }, [mentions, onMentionsChange]);

  React.useEffect(() => {
    if (enableHyperlinks && onHyperlinksChange) {
      const hyperlinks = extractHyperlinks(value);
      onHyperlinksChange(hyperlinks);
    }
  }, [value, enableHyperlinks, onHyperlinksChange, extractHyperlinks]);

  return {
    textareaRef,
    popupState,
    users,
    loading,
    mentions,
    selectedIndex,
    handleChange,
    handleKeyDownEvent,
    handleBlur,
    handleMentionSelect,
    closePopup,
    hasActionBar: hasActionBarFlag,
    effectiveOnSendClick,
    handlePlusMenuSelect,
    actionBarConfigForBar
  } as const;
}
